/**
 * GPS Connection State Machine
 * Manages GPS WebSocket connection state transitions
 */

/**
 * GPS connection states
 */
export type GpsConnectionState = 
  | 'idle' 
  | 'connecting' 
  | 'connected' 
  | 'reconnecting'
  | 'disconnected' 
  | 'failed';

/**
 * GPS connection events
 */
export type GpsConnectionEvent = 
  | 'CONNECT'
  | 'CONNECTION_ESTABLISHED'
  | 'CONNECTION_LOST'
  | 'RECONNECT_ATTEMPT'
  | 'DISCONNECT'
  | 'FAILURE'
  | 'RESET';

/**
 * State transition rule
 */
interface GpsStateTransition {
  from: GpsConnectionState;
  event: GpsConnectionEvent;
  to: GpsConnectionState;
  guard?: () => boolean;
}

/**
 * GPS Connection State Machine class
 * Ensures valid state transitions for GPS WebSocket connections
 */
export class GpsConnectionStateMachine {
  private currentState: GpsConnectionState = 'idle';
  private transitions: GpsStateTransition[] = [
    // Basic connection flow
    { from: 'idle', event: 'CONNECT', to: 'connecting' },
    { from: 'connecting', event: 'CONNECTION_ESTABLISHED', to: 'connected' },
    { from: 'connecting', event: 'FAILURE', to: 'failed' },
    
    // Connection loss and reconnection
    { from: 'connected', event: 'CONNECTION_LOST', to: 'reconnecting' },
    { from: 'reconnecting', event: 'CONNECTION_ESTABLISHED', to: 'connected' },
    { from: 'reconnecting', event: 'FAILURE', to: 'failed' },
    { from: 'reconnecting', event: 'RECONNECT_ATTEMPT', to: 'connecting' },
    
    // Manual disconnect
    { from: 'connected', event: 'DISCONNECT', to: 'disconnected' },
    { from: 'connecting', event: 'DISCONNECT', to: 'disconnected' },
    { from: 'reconnecting', event: 'DISCONNECT', to: 'disconnected' },
    { from: 'failed', event: 'DISCONNECT', to: 'disconnected' },
    
    // Reconnection from different states
    { from: 'disconnected', event: 'CONNECT', to: 'connecting' },
    { from: 'failed', event: 'CONNECT', to: 'connecting' },
    
    // Reset to idle
    { from: 'disconnected', event: 'RESET', to: 'idle' },
    { from: 'failed', event: 'RESET', to: 'idle' },
  ];

  private callbacks: {
    onStateChange?: (newState: GpsConnectionState, previousState: GpsConnectionState, event: GpsConnectionEvent) => void;
    onInvalidTransition?: (currentState: GpsConnectionState, event: GpsConnectionEvent) => void;
  } = {};

  /**
   * Get current state
   */
  getState(): GpsConnectionState {
    return this.currentState;
  }

  /**
   * Check if transition is valid
   */
  canTransition(event: GpsConnectionEvent): boolean {
    return this.transitions.some(
      t => t.from === this.currentState && 
           t.event === event && 
           (!t.guard || t.guard())
    );
  }

  /**
   * Execute state transition
   */
  transition(event: GpsConnectionEvent): boolean {
    const transition = this.transitions.find(
      t => t.from === this.currentState && 
           t.event === event && 
           (!t.guard || t.guard())
    );

    if (!transition) {
      this.callbacks.onInvalidTransition?.(this.currentState, event);
      return false;
    }

    const previousState = this.currentState;
    this.currentState = transition.to;
    
    this.callbacks.onStateChange?.(this.currentState, previousState, event);
    
    return true;
  }

  /**
   * Set state change callbacks
   */
  setCallbacks(callbacks: {
    onStateChange?: (newState: GpsConnectionState, previousState: GpsConnectionState, event: GpsConnectionEvent) => void;
    onInvalidTransition?: (currentState: GpsConnectionState, event: GpsConnectionEvent) => void;
  }): void {
    this.callbacks = callbacks;
  }

  /**
   * Reset to idle state
   */
  reset(): void {
    const previousState = this.currentState;
    this.currentState = 'idle';
    this.callbacks.onStateChange?.('idle', previousState, 'RESET');
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.currentState === 'connected';
  }

  /**
   * Check if currently connecting or reconnecting
   */
  isConnecting(): boolean {
    return this.currentState === 'connecting' || this.currentState === 'reconnecting';
  }

  /**
   * Check if in error state
   */
  hasFailed(): boolean {
    return this.currentState === 'failed';
  }

  /**
   * Get human-readable state description
   */
  getStateDescription(): string {
    const descriptions: Record<GpsConnectionState, string> = {
      'idle': 'Not connected',
      'connecting': 'Establishing connection',
      'connected': 'Connected and ready',
      'reconnecting': 'Attempting to reconnect',
      'disconnected': 'Disconnected',
      'failed': 'Connection failed'
    };

    return descriptions[this.currentState];
  }
}